import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BRAND, Icon, Button, Card, Frame,
} from '../../../components/onboarding/primitives';
import { onboardingAPI } from '../../../services/api';

/**
 * Props contract:
 *   state            — full onboarding state object. Reads state.activeOrder
 *                      and state.s1?.nameAr for the summary.
 *   rejected         — boolean. Show rejection view when true.
 *   rejectionReason  — string (optional). Arabic reason from backend.
 *   goToStep         — (n: number) => void.
 *
 * API endpoints used:
 *   onboardingAPI.getStatus()
 *     — GET /onboarding/status
 *     200 -> { status: 'pending' | 'active' | 'awaiting_receipt' | ..., ... }
 *     When status === 'active', redirect to '/' (dashboard).
 */
export default function Step8Pending({ state, rejected, rejectionReason, goToStep }) {
  const navigate = useNavigate();
  const order = state?.activeOrder || {};

  const total = Number(order.total || 0);
  // Backend populates `planId` into a full Plan doc, and ALSO stamps
  // `planNameAr` / `planSlug` on the order as a frozen snapshot. Prefer the
  // populated doc's Arabic name, fall back to the snapshot, then the slug.
  const planDoc = order.planId && typeof order.planId === 'object' ? order.planId : null;
  const planName = planDoc?.nameAr
    || planDoc?.name
    || order.planNameAr
    || order.plan?.nameAr
    || order.plan?.name
    || order.planSlug
    || order.plan?.slug
    || '—';
  const cycleLabel = order.billingCycle === 'monthly' ? 'شهري' : 'سنوي';
  const labName = state?.s1?.nameAr
    || state?.s2?.nameAr
    || state?.backendSnapshot?.nameAr
    || state?.backendSnapshot?.name
    || '—';

  // Poll once on mount to check if the backend already activated the tenant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await onboardingAPI.getStatus();
        if (cancelled) return;
        const payload = res.data?.data || res.data || {};
        const nextStep = payload.nextStep;
        if (nextStep === 'dashboard') {
          navigate('/');
        } else if (nextStep === 'activated') {
          goToStep(9);
        }
      } catch {
        /* silent — stay on this screen */
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, goToStep]);

  if (rejected) {
    return (
      <Frame>
        <div style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: 50,
              background: 'rgba(226,75,74,0.1)',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              stroke={BRAND.error}
              strokeWidth="3"
              strokeLinecap="round"
            >
              <path d="M12 12l16 16M28 12L12 28" />
            </svg>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 12px' }}>
            للأسف لم نتمكن من مصادقة التحويل
          </h1>
          <p
            style={{
              fontSize: 15,
              color: BRAND.muted,
              margin: '0 0 24px',
              lineHeight: 1.7,
            }}
          >
            راجع فريقنا إيصالك لكن لم نتمكن من اعتماد التحويل. يمكنك رفع إيصال جديد في أي وقت.
          </p>

          {rejectionReason && (
            <div
              style={{
                background: 'rgba(226,75,74,0.06)',
                border: `1px solid rgba(226,75,74,0.2)`,
                borderRadius: 14,
                padding: 18,
                textAlign: 'start',
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: BRAND.error,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                سبب الرفض
              </div>
              <div style={{ fontSize: 14, color: BRAND.text, lineHeight: 1.7 }}>
                {rejectionReason}
              </div>
            </div>
          )}

          <Button onClick={() => goToStep(7)} iconLeft={<Icon.Upload size={16} />}>
            رفع إيصال جديد
          </Button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame maxWidth={680}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div
          style={{
            width: 110,
            height: 110,
            borderRadius: 55,
            background: BRAND.mint,
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 55,
              border: `2px solid ${BRAND.green}`,
              animation: 'pulse 2s infinite',
            }}
          />
          <div style={{ color: BRAND.greenDark }}>
            <Icon.Clock size={44} />
          </div>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 10px', letterSpacing: -0.5 }}>
          تم استلام طلبك بنجاح
        </h1>
        <p
          style={{
            fontSize: 15,
            color: BRAND.muted,
            lineHeight: 1.7,
            maxWidth: 480,
            margin: '0 auto',
          }}
        >
          فريقنا يراجع إيصال التحويل الآن. سيصلك إيميل وSMS خلال{' '}
          <b style={{ color: BRAND.text }}>24 ساعة كحد أقصى</b> بمجرد المصادقة، وسيتم تفعيل حسابك
          تلقائياً.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card padding={20}>
          <div
            style={{
              fontSize: 12,
              color: BRAND.muted,
              marginBottom: 10,
              fontWeight: 500,
            }}
          >
            ملخّص الطلب
          </div>
          <SummaryLine label="المختبر" value={labName} />
          <SummaryLine label="الباقة" value={`${planName} — ${cycleLabel}`} />
          <SummaryLine label="المبلغ" value={`${total.toLocaleString()} ر.س`} bold />
        </Card>

        <Card padding={20}>
          <div
            style={{
              fontSize: 12,
              color: BRAND.muted,
              marginBottom: 10,
              fontWeight: 500,
            }}
          >
            سير العملية
          </div>
          <Timeline />
        </Card>
      </div>

      <Card padding={20} style={{ background: BRAND.surfaceAlt, border: 'none' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>أثناء انتظارك</div>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {[
            'جهّز شعار المختبر بصيغة PNG أو SVG',
            'اجمع بيانات فروعك والأجهزة الطبية',
            'تصفّح مركز المساعدة للتعرّف على النظام',
          ].map((t, i) => (
            <li
              key={i}
              style={{
                fontSize: 13.5,
                color: BRAND.text,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ width: 4, height: 4, borderRadius: 2, background: BRAND.green }} />
              {t}
            </li>
          ))}
        </ul>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <Button variant="ghost" onClick={() => goToStep(0)}>
          تسجيل الخروج
        </Button>
        <a
          style={{
            fontSize: 13,
            color: BRAND.greenDark,
            fontWeight: 500,
            alignSelf: 'center',
          }}
          href="mailto:support@listechs.com"
        >
          هل لديك سؤال؟ تواصل مع الدعم
        </a>
      </div>
    </Frame>
  );
}

function SummaryLine({ label, value, bold }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0',
        borderBottom: `1px solid ${BRAND.surfaceAlt}`,
        fontSize: 13.5,
      }}
    >
      <span style={{ color: BRAND.muted }}>{label}</span>
      <span
        style={{
          color: BRAND.text,
          fontWeight: bold ? 700 : 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Timeline() {
  const steps = [
    { label: 'إنشاء الحساب', done: true },
    { label: 'رفع الإيصال', done: true },
    { label: 'مصادقة الإدارة', active: true },
    { label: 'تفعيل النظام', done: false },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                background: s.done ? BRAND.green : s.active ? '#fff' : BRAND.surfaceAlt,
                border: `${s.active ? 2 : 1}px solid ${s.done || s.active ? BRAND.green : BRAND.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: s.active ? `0 0 0 5px ${BRAND.mint}` : 'none',
                position: 'relative',
              }}
            >
              {s.done && <Icon.Check color="#fff" size={12} />}
              {s.active && (
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    background: BRAND.green,
                    animation: 'pulse 1.5s infinite',
                  }}
                />
              )}
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  width: 1,
                  height: 16,
                  background: s.done ? BRAND.green : BRAND.border,
                }}
              />
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              color: s.active ? BRAND.text : s.done ? BRAND.text : BRAND.muted,
              fontWeight: s.active ? 600 : 400,
              paddingBottom: i < steps.length - 1 ? 16 : 0,
            }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}
